/**
 * The boxes the subcontractor packet is built from.
 *
 * Each one shows, under it, what the `crew-packet` function said about it,
 * and is marked invalid while it does, so a refusal lands beside the box it
 * is about rather than in a list at the top.
 */

import type { WORDS } from "./words";

export type Answers = Record<string, string | boolean | undefined>;

/** Everything a box needs from the form it sits in. */
export interface PacketForm {
  answers: Answers;
  errors: Record<string, string>;
  words: (typeof WORDS)["en"] | (typeof WORDS)["es"];
  set: (field: string, value: string | boolean) => void;
}

export function textOf(form: PacketForm, field: string): string {
  const value = form.answers[field];
  return typeof value === "string" ? value : "";
}

function Problem({ form, field }: { form: PacketForm; field: string }) {
  const message = form.errors[field];
  return message ? (
    <span className="cpk-error" id={`cpk-${field}-error`}>
      {message}
    </span>
  ) : null;
}

/** A labelled text box. */
export function Box({
  form,
  field,
  label,
  hint,
  type = "text",
  inputMode,
  autoComplete = "off",
  placeholder,
  maxLength,
}: {
  form: PacketForm;
  field: string;
  label: string;
  hint?: string;
  type?: string;
  inputMode?: "numeric" | "tel" | "email" | "text";
  autoComplete?: string;
  placeholder?: string;
  maxLength?: number;
}) {
  const invalid = Boolean(form.errors[field]);
  return (
    <label className="ilink-field" htmlFor={`cpk-${field}`}>
      {label}
      <input
        id={`cpk-${field}`}
        type={type}
        inputMode={inputMode}
        autoComplete={autoComplete}
        placeholder={placeholder}
        maxLength={maxLength}
        value={textOf(form, field)}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? `cpk-${field}-error` : undefined}
        onChange={(e) => form.set(field, e.target.value)}
      />
      {hint ? <span className="cpk-hint">{hint}</span> : null}
      <Problem form={form} field={field} />
    </label>
  );
}

/** A labelled drop-down. */
export function Pick({
  form,
  field,
  label,
  options,
}: {
  form: PacketForm;
  field: string;
  label: string;
  options: Record<string, string>;
}) {
  return (
    <label className="ilink-field" htmlFor={`cpk-${field}`}>
      {label}
      <select
        id={`cpk-${field}`}
        value={textOf(form, field)}
        aria-invalid={form.errors[field] ? true : undefined}
        onChange={(e) => form.set(field, e.target.value)}
      >
        <option value="" disabled>
          —
        </option>
        {Object.entries(options).map(([code, text]) => (
          <option key={code} value={code}>
            {text}
          </option>
        ))}
      </select>
      <Problem form={form} field={field} />
    </label>
  );
}

/**
 * A question answered with chips, which a thumb hits more easily than a
 * radio button.
 */
export function Chips<T extends string | boolean>({
  form,
  field,
  question,
  choices,
}: {
  form: PacketForm;
  field: string;
  question: string;
  choices: { value: T; label: string }[];
}) {
  return (
    <div className="cpk-question">
      <span className="cpk-q" id={`cpk-${field}`}>
        {question}
      </span>
      <div
        className="ilink-chips"
        role="group"
        aria-labelledby={`cpk-${field}`}
      >
        {choices.map((choice) => (
          <button
            key={String(choice.value)}
            type="button"
            className="ilink-chip"
            aria-pressed={form.answers[field] === choice.value}
            aria-invalid={form.errors[field] ? true : undefined}
            onClick={() => form.set(field, choice.value)}
          >
            {choice.label}
          </button>
        ))}
      </div>
      <Problem form={form} field={field} />
    </div>
  );
}

/** A tick box with its sentence beside it. */
export function Tick({
  form,
  field,
  label,
}: {
  form: PacketForm;
  field: string;
  label: string;
}) {
  return (
    <>
      <label className="cpk-tick" htmlFor={`cpk-${field}`}>
        <input
          id={`cpk-${field}`}
          type="checkbox"
          checked={form.answers[field] === true}
          aria-invalid={form.errors[field] ? true : undefined}
          onChange={(e) => form.set(field, e.target.checked)}
        />
        <span>{label}</span>
      </label>
      <Problem form={form} field={field} />
    </>
  );
}
